// Curated, user-facing release notes for the "What's New" modal.
//
// IMPORTANT: This is decoupled from the auto-incremented build number. Every
// `npm run build` bumps APP_VERSION (often for internal-only tweaks), but the
// "What's New" modal only triggers for versions that have an entry here. Add an
// entry ONLY for releases worth announcing to anglers, newest first.
//
// Shape:
//   {
//     version: "1.0.521",          // the APP_VERSION this shipped in
//     date: "2026-06-30",          // YYYY-MM-DD, display only
//     title: "Short headline",     // one skimmable line
//     notes: ["Plain-language bullet", "Another bullet"],
//   }

export const CHANGELOG = [
  {
    version: "1.0.654",
    date: "2026-07-28",
    title: "Rate the99",
    notes: [
      "Enjoying the app? There's now a \"Rate the99\" button in User Preferences that takes you straight to the store to leave a rating.",
      "After you wrap up a trip, we may occasionally ask for a quick rating — never more than once in a while.",
    ],
  },
  {
    version: "1.0.652",
    date: "2026-07-24",
    title: "Tournament trips: automatic reminders",
    notes: [
      "When you're registered for a tournament, your session now shows up under Planned Trips automatically.",
      "You'll get the same reminders as trips you schedule yourself — a heads-up an hour before, and a \"time to fish\" prompt at the start.",
      "If a tournament is cancelled or you're removed, its reminders clear themselves.",
    ],
  },
  {
    version: "1.0.649",
    date: "2026-07-19",
    title: "Tournament roles: a focused, region-aware view",
    notes: [
      "Tournament Directors, Spectators, and Sponsors now see just the Leaderboard and Support — a cleaner, view-only experience.",
      "Directors see only the tournaments they run.",
      "Spectators and Sponsors can browse and search other tournaments by region (state/province).",
    ],
  },
  {
    version: "1.0.637",
    date: "2026-07-19",
    title: "Scheduled sessions: reminders + auto-start",
    notes: [
      "Schedule a future session and you'll get a reminder about an hour before, and again when it's time to start.",
      "When it's time, the app can start your session automatically — or tap Start right from the notification.",
      "Plan ahead: add your trip dates from the dashboard and we'll handle the reminders.",
    ],
  },
  {
    version: "1.0.635",
    date: "2026-07-17",
    title: "Your profile photo, across the app",
    notes: [
      "Your profile photo now appears on your Trip Report, during an active session, and on your Proof of Catch.",
      "In tournaments, anglers' photos show next to their certified catch videos.",
      "Tap your photo on the dashboard to see it larger, along with your name and team.",
    ],
  },
  {
    version: "1.0.634",
    date: "2026-07-17",
    title: "Add a profile photo",
    notes: [
      "You can now set a profile photo. Open Settings → User Preferences → Profile Photo and tap Add photo (or Change) to pick one.",
      "Your photo appears as a small round icon at the top of your dashboard. You can update or remove it anytime.",
    ],
  },
  {
    version: "1.0.572",
    date: "2026-07-04",
    title: "Post your certified catch to THE 99 on YouTube",
    notes: [
      "If you've turned on video sharing, you'll now be asked — right after a clip is certified — whether you'd like to post that catch publicly to THE 99's YouTube channel.",
      "It's always your choice, one clip at a time: nothing is posted to YouTube unless you tap the button. Turn video sharing on or off anytime in User Preferences.",
    ],
  },
  {
    version: "1.0.571",
    date: "2026-07-02",
    title: "Conditions Timeline on your Trip Report",
    notes: [
      "Your Trip Report now has a collapsible Conditions Timeline that graphs barometric pressure, wind, tide stage, and solunar windows across your whole session.",
      "Each fish shows up as a numbered dot on the graphs — same number and color as its pin on the map — so you can see exactly what the conditions were doing when you got the bite. Hover a dot to see the species.",
    ],
  },
  {
    version: "1.0.570",
    date: "2026-07-02",
    title: "Resumed-trip label",
    notes: [
      "When a trip is picked back up after a reload or re-login, the recording screen now shows a \"Resumed from previous login\" tag next to the timer, so it's clear the clock is counting from when the trip really started.",
    ],
  },
  {
    version: "1.0.569",
    date: "2026-07-02",
    title: "Share link fix",
    notes: [
      "Sharing a catch video by text now opens your device's share menu, so it works on tablets too — not just phones with a texting app.",
    ],
  },
  {
    version: "1.0.568",
    date: "2026-07-02",
    title: "Tide forecast fix",
    notes: [
      "The 3-day tide forecast now opens neatly within the app instead of sliding off the edge of the screen on larger displays.",
    ],
  },
  {
    version: "1.0.567",
    date: "2026-07-02",
    title: "Cleaner Proof of Catch",
    notes: [
      "The Proof of Catch recording view is less cluttered — the extra back button is gone, and the \"Tournament Mode\" tag only appears when you're actually fishing a tournament.",
    ],
  },
  {
    version: "1.0.566",
    date: "2026-07-02",
    title: "Accurate resumed timer",
    notes: [
      "When you return to a trip that ended unexpectedly, the timer now picks up at the real time elapsed since you started — no more resetting back to zero.",
    ],
  },
  {
    version: "1.0.565",
    date: "2026-07-02",
    title: "GoPro scan fix",
    notes: [
      "The \"Scan for GoPro\" button now works as soon as you open the GoPro screen — tapping it turns on Bluetooth access and prompts for permission if needed, instead of staying greyed out.",
    ],
  },
  {
    version: "1.0.564",
    date: "2026-07-02",
    title: "Consistent buttons",
    notes: [
      "Buttons across the app now share one consistent style — uniform shape, spacing, and all-caps labels — so actions look and feel the same everywhere.",
    ],
  },
  {
    version: "1.0.560",
    date: "2026-07-02",
    title: "Smoother GoPro review",
    notes: [
      "The Confirm & Certify screen now has a Cancel button, and Re-record matches the app's button style.",
      "GoPro clip times now display in your local time.",
      "Video previews load reliably before upload, and clips are only marked uploaded once the upload actually completes.",
    ],
  },
  {
    version: "1.0.550",
    date: "2026-07-01",
    title: "GoPro & voice upgrades",
    notes: [
      "Your GoPro clip list now shows a thumbnail preview of each video.",
      "Listing clips is faster and no longer flickers or reloads.",
      "A voice now announces when your GoPro connects or disconnects, plus a distinct alarm on a dropped connection.",
      "The Confirm & Certify screen has a “Back to GoPro list” button.",
      "Voice commands are more reliable — “stop recording” works even if it’s misheard, and you’ll hear a chime when listening starts.",
    ],
  },
  {
    version: "1.0.521",
    date: "2026-06-30",
    title: "Tell us where you fish",
    notes: [
      "Choose your country during setup — your flag now shows in the header.",
      "Your mobile number now formats automatically for your country.",
    ],
  },
  {
    version: "1.0.520",
    date: "2026-06-30",
    title: "A steadier session timer",
    notes: [
      "Your active session timer keeps counting when you move between screens.",
      "The timer now shows hours, minutes, and seconds.",
    ],
  },
];

// Parse "1.0.523" -> [1, 0, 523]. Non-numeric / malformed parts become 0.
function parseVersion(version) {
  return String(version || "")
    .split(".")
    .map((part) => {
      const n = Number(part);
      return Number.isFinite(n) ? n : 0;
    });
}

// Returns > 0 if a is newer than b, < 0 if older, 0 if equal.
export function compareVersions(a, b) {
  const pa = parseVersion(a);
  const pb = parseVersion(b);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff !== 0) return diff > 0 ? 1 : -1;
  }
  return 0;
}

// Changelog entries strictly newer than `lastSeenVersion`, newest first.
// When `lastSeenVersion` is falsy, returns [] — first-run users get no history.
export function getUnseenEntries(lastSeenVersion) {
  if (!lastSeenVersion) return [];
  return CHANGELOG
    .filter((entry) => compareVersions(entry.version, lastSeenVersion) > 0)
    .sort((a, b) => compareVersions(b.version, a.version));
}
